<template>
  <div class="rm-screen rm-taken">
    <div v-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>

    <template v-else>
      <div class="rm-summary">
        <div class="rm-summary__row">
          <div class="rm-summary__labels">
            <span>{{ $t("taken") }}</span>
            <span>
              {{ pluralT("report_mobile.companies_n", summary.companies) }} ·
              {{ pluralT("report_mobile.meals_n", summary.ordered) }}
            </span>
          </div>
          <span class="rm-summary__value">
            {{ summary.taken }}<span class="rm-summary__value-of"> / {{ summary.ordered }}</span>
          </span>
        </div>
        <div
          class="rm-progress rm-progress--inverse"
          role="progressbar"
          :aria-valuenow="summary.taken"
          aria-valuemin="0"
          :aria-valuemax="summary.ordered"
        >
          <div class="rm-progress__fill" :style="{ width: `${summary.percent}%` }"></div>
        </div>
      </div>

      <article v-for="company in takenCompanies" :key="company.key" class="rm-card rm-taken-card">
        <div class="rm-taken-card__head">
          <div class="rm-taken-card__title">
            <h2 class="rm-taken-card__name">{{ company.name }}</h2>
          </div>
          <span class="rm-taken-card__value rm-num">
            {{ company.taken }}<span class="rm-taken-card__of"> / {{ company.ordered }}</span>
          </span>
        </div>
        <div
          class="rm-progress"
          :class="{ 'rm-progress--complete': company.complete }"
          role="progressbar"
          :aria-valuenow="company.taken"
          aria-valuemin="0"
          :aria-valuemax="company.ordered"
        >
          <div class="rm-progress__fill" :style="{ width: `${company.percent}%` }"></div>
        </div>
        <button
          type="button"
          class="rm-btn rm-btn--sm"
          @click="openDialogReportFirm(company.source)"
        >
          <v-icon size="18" color="#1c1917">mdi-calendar-month-outline</v-icon>
          {{ $t("monthly") }}
        </button>
      </article>

      <div v-if="!takenCompanies.length" class="rm-empty">
        {{
          filters.search
            ? $t("report_mobile.no_results_for", { search: filters.search })
            : $t("no_taken_meals_data")
        }}
      </div>
    </template>

    <div v-if="canScan" class="rm-taken__scan">
      <button type="button" class="rm-btn rm-btn--primary rm-taken__scan-btn" @click="openScanner">
        <v-icon size="22" color="#ffffff">mdi-qrcode-scan</v-icon>
        {{ $t("report_mobile.scan_worker_qr") }}
      </button>
    </div>

    <report-meals-taken-company-monthly-orders
      v-model="dialogFirm"
      :company-data="firmReportItem"
    ></report-meals-taken-company-monthly-orders>
  </div>
</template>

<script>
import ReportMealsTabTaken from "@/components/report/meals/tabs/ReportMealsTabTaken.vue";
import hasPlural from "../hasPlural";

const SCANNER_ROUTE = "canteen";

/**
 * Mobilni prikaz "Preuzeto": nasleđuje učitavanje, mesečni dijalog, štampu i Excel
 * iz ReportMealsTabTaken.
 */
export default {
  name: "ReportMobileTaken",
  extends: ReportMealsTabTaken,
  mixins: [hasPlural],
  props: {
    shifts: {
      type: Array,
      required: true,
    },
  },
  computed: {
    takenCompanies() {
      const searched = this.customSearch(
        this.filteredTotal,
        [{ value: "menu" }],
        this.filters.search
      );
      return searched
        .map((item) => this.toTakenCompany(item))
        .filter((company) => company.ordered > 0 || company.taken > 0);
    },
    summary() {
      const companies = this.filteredTotal
        .map((item) => this.parseTaken(item.total))
        .filter((total) => total.ordered > 0 || total.taken > 0);
      const taken = companies.reduce((sum, total) => sum + total.taken, 0);
      const ordered = companies.reduce((sum, total) => sum + total.ordered, 0);
      return {
        companies: companies.length,
        taken,
        ordered,
        percent: this.percentOf(taken, ordered),
      };
    },
    canScan() {
      const userType = this.$store.state.user.user?.type;
      const route = this.$router.options.routes.find((item) => item.name === SCANNER_ROUTE);
      return Boolean(route && route.roles?.includes(userType));
    },
  },
  watch: {
    shifts: {
      handler(shifts) {
        this.selectedShifts = [...shifts];
      },
      immediate: true,
    },
  },
  methods: {
    toTakenCompany(item) {
      const total = this.parseTaken(item.total);
      return {
        key: item.num,
        source: item,
        name: item.menu,
        taken: total.taken,
        ordered: total.ordered,
        complete: total.ordered > 0 && total.taken >= total.ordered,
        percent: this.percentOf(total.taken, total.ordered),
      };
    },
    /** "3/22" → { taken: 3, ordered: 22 } */
    parseTaken(value) {
      if (typeof value === "number") {
        return { taken: value, ordered: 0 };
      }
      const [taken, ordered] = String(value || "0/0").split("/");
      return { taken: parseInt(taken) || 0, ordered: parseInt(ordered) || 0 };
    },
    percentOf(part, whole) {
      return whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0;
    },
    openScanner() {
      this.$router.push({ name: SCANNER_ROUTE });
    },
  },
};
</script>

<style lang="scss">
.rm-taken {
  min-height: calc(100vh - 220px);
}

.rm-theme .rm-taken-card {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  &__title {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    font-size: 18px;
    font-weight: 700;
  }

  &__value {
    font-size: 22px;
    font-weight: 700;
    white-space: nowrap;
  }

  &__of {
    font-size: 15px;
    color: var(--rm-muted);
  }
}

.rm-taken__scan {
  position: sticky;
  bottom: 0;
  margin: auto -16px -24px;
  padding: 12px 16px calc(20px + env(safe-area-inset-bottom));
  background: var(--rm-bg);
  border-top: 1px solid var(--rm-border);
}

.rm-theme .rm-taken__scan-btn {
  height: 56px;
  border-radius: 16px;
}
</style>
